import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { getActualMetrics } from '../../../services/planningFinance';
import { getProjectDisplayName } from '../../../utils/planningDisplay';

const CompleteProjectModal = ({ project, property, onSave, onCancel }) => {
  const { formatCurrency } = useCurrency();
  const [formData, setFormData] = useState({
    actual_cost: '',
    actual_end_date: '',
    completion_notes: ''
  });
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    if (project) {
      setFormData({
        actual_cost: project?.actual_cost?.toString() || '',
        actual_end_date: project?.actual_end_date || '',
        completion_notes: project?.completion_notes || ''
      });
    }
  }, [project]);

  // Calculate planning metrics when actual cost changes
  useEffect(() => {
    if (property && formData?.actual_cost) {
      const m = getActualMetrics({
        ...project,
        actual_cost: formData.actual_cost
      }, property);
      setMetrics(m);
    } else {
      setMetrics(null);
    }
  }, [formData?.actual_cost, project, property]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...project,
      status: 'completed',
      actual_cost: parseFloat(formData.actual_cost) || null,
      actual_end_date: formData.actual_end_date || null,
      completion_notes: formData.completion_notes || ''
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 'var(--spacing-md)'
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        onCancel();
      }
    }}
    >
      <div style={{
        background: 'var(--color-card)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: 'var(--shadow-lg)'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: 'var(--spacing-lg)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'var(--color-success)/10',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <div>
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                margin: 0
              }}>
                Finaliser le projet
              </h2>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                color: 'var(--color-muted-foreground)',
                margin: 0
              }}>
                {getProjectDisplayName(project, property)}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onCancel}
          />
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 'var(--spacing-lg)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <Input
              label="Coût réel du projet"
              type="number"
              placeholder="Ex: 15000"
              value={formData.actual_cost}
              onChange={(e) => handleFieldChange('actual_cost', e.target.value)}
              min="0"
              step="0.01"
              required
            />

            <Input
              label="Date de fin réelle"
              type="date"
              value={formData.actual_end_date}
              onChange={(e) => handleFieldChange('actual_end_date', e.target.value)}
            />

            <div>
              <label style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)',
                display: 'block'
              }}>
                Notes de complétion (optionnel)
              </label>
              <textarea
                value={formData.completion_notes}
                onChange={(e) => handleFieldChange('completion_notes', e.target.value)}
                placeholder="Ajoutez des notes sur la complétion du projet..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-background)',
                  color: 'var(--color-foreground)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {metrics && (metrics.realYield != null || metrics.recoveryRate != null) && (
              <div style={{
                padding: 'var(--spacing-md)',
                background: 'var(--color-muted)/30',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-xs)'
              }}>
                {metrics.valueCreation != null && metrics.valueCreation > 0 && (
                  <>
                    <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', margin: 0 }}>Valeur créée</p>
                    <p style={{ fontFamily: 'var(--font-data)', fontSize: '1rem', fontWeight: 600, color: 'var(--color-success)', margin: 0 }}>{formatCurrency(metrics.valueCreation)}</p>
                  </>
                )}
                {metrics.realYield != null && metrics.realYield > 0 && (
                  <>
                    <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', margin: 0 }}>Rendement réel</p>
                    <p style={{ fontFamily: 'var(--font-data)', fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-success)', margin: 0 }}>{(metrics.realYield * 100).toFixed(1)}%</p>
                  </>
                )}
                {metrics.recoveryRate != null && metrics.recoveryRate > 0 && (
                  <>
                    <p style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)', margin: 0 }}>Taux de récupération du capital</p>
                    <p style={{ fontFamily: 'var(--font-data)', fontSize: '1rem', fontWeight: 600, color: 'var(--color-foreground)', margin: 0 }}>{(metrics.recoveryRate * 100).toFixed(0)}%</p>
                  </>
                )}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: 'var(--spacing-md)',
              marginTop: 'var(--spacing-md)',
              paddingTop: 'var(--spacing-md)',
              borderTop: '1px solid var(--color-border)'
            }}>
              <Button
                variant="outline"
                fullWidth
                onClick={onCancel}
              >
                Annuler
              </Button>
              <Button
                variant="default"
                fullWidth
                type="submit"
                iconName="CheckCircle"
                iconPosition="left"
              >
                Finaliser
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteProjectModal;
